import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { AppContext } from '@prisjakt/ui-app-context';
import Currency, { formatCurrency } from '@prisjakt/ui-currency';
import Graph from '../../../../components/graph';
import PriceHistoryCurve from '../../../../components/graph/components/price-history-curve';
import PricePrediction from '../../../../components/graph/components/price-prediction';

import * as S from './styles';

const sixMonthsAgo = (() => {
  const date = new Date();
  date.setMonth(date.getMonth() - 6);
  return date.valueOf();
})();

const isFromLastSixMonths = node => {
  const [year, month, day] = node.date.split('-');
  return new Date(year, month - 1, day).valueOf() > sixMonthsAgo;
};

const formatDate = d =>
  `${d.getFullYear()}-${`0${d.getMonth() + 1}`.slice(
    -2
  )}-${`0${d.getDate()}`.slice(-2)}`;

const PriceHistorySummary = ({ product, isLoading }) => {
  const { t, market } = useContext(AppContext);

  if (isLoading) {
    return <S.PriceHistorySummary isLoading />;
  }

  const lowestPriceNodes = product.statistics.nodes.filter(isFromLastSixMonths);

  const graphs = [
    {
      name: 'price-history',
      legend: {
        label: t('product', 'price-history.actual-price'),
        color: '#4895B6',
        lineType: 'normal',
      },
      render: graphProps => (
        <PriceHistoryCurve
          key={graphProps.name}
          data={lowestPriceNodes}
          {...graphProps}
        />
      ),
    },
  ];

  let xScaleData = lowestPriceNodes.map(({ date }) => new Date(date));
  let yScaleData = lowestPriceNodes.map(({ lowestPrice }) => lowestPrice);

  if (product.priceForecast) {
    graphs.push({
      name: 'price-prediction',
      legend: {
        label: t('product', 'price-history.pre-release'),
        color: '#5fbc40',
        lineType: 'dashed',
      },
      render: graphProps => (
        <PricePrediction
          key={graphProps.name}
          data={product.priceForecast}
          {...graphProps}
        />
      ),
    });

    xScaleData = xScaleData.concat(
      product.priceForecast.map(({ date }) => new Date(date))
    );
    yScaleData = yScaleData.concat(
      product.priceForecast.map(({ price }) => price)
    );
  }

  return (
    <S.PriceHistorySummary>
      <S.FadingBox>
        <S.PriceHistoryHeading>
          {t('product', 'price-history.title')}
        </S.PriceHistoryHeading>
        <S.CurrentPrice>
          {t('product', 'price-history.current-lowest-price', {
            lowestPrice: (
              <Currency
                key="lowestPrice"
                price={
                  product.priceSummary.regular ||
                  product.priceSummary.alternative
                }
              />
            ),
          })}
        </S.CurrentPrice>
        <S.PriceHistoryWrapper>
          {IS_BROWSER && (
            <>
              <Graph
                width={280}
                aspectRatio={3}
                showXGrid={false}
                showYGrid={false}
                showXAxis={false}
                showYAxis={false}
                showLegend={false}
                xTickFormat={value => formatDate(value)}
                yTickFormat={value => formatCurrency(value, market.code)}
                renderTooltip={({ xValue, yValue }) => (
                  <>
                    <div style={{ fontWeight: 'bold', marginBottom: 5 }}>
                      {xValue}
                    </div>
                    <div>
                      <Currency price={yValue} market={market.code} />
                    </div>
                  </>
                )}
                xScaleData={xScaleData}
                yScaleData={yScaleData}
                graphs={graphs}
                nodes={
                  product.priceForecast
                    ? [
                        product.statistics.nodes
                          .filter(isFromLastSixMonths)
                          .map(item => ({
                            date: item.date,
                            value: item.lowestPrice,
                          })),
                        product.priceForecast.map(item => ({
                          date: item.date,
                          value: item.price,
                        })),
                      ]
                    : [
                        product.statistics.nodes
                          .filter(isFromLastSixMonths)
                          .map(item => ({
                            date: item.date,
                            value: item.lowestPrice,
                          })),
                      ]
                }
              />
            </>
          )}
        </S.PriceHistoryWrapper>
      </S.FadingBox>
    </S.PriceHistorySummary>
  );
};

PriceHistorySummary.propTypes = {
  product: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default memo(PriceHistorySummary);
